import { Plus, Trash2 } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { type GradientConfig, gradientToCss } from "@/lib/tier";

type Props = {
  value: GradientConfig;
  onChange: (next: GradientConfig) => void;
};

const MIN_STOPS = 3;
const MAX_STOPS = 5;

const PRESETS: { name: string; config: GradientConfig }[] = [
  {
    name: "Sunset",
    config: { angle: 135, smooth: true, stops: [{ hex: "#FFD3A5", pos: 0 }, { hex: "#FD9F87", pos: 50 }, { hex: "#C56FA8", pos: 100 }] },
  },
  {
    name: "Ocean",
    config: { angle: 160, smooth: true, stops: [{ hex: "#A8E6F0", pos: 0 }, { hex: "#5BA8C9", pos: 50 }, { hex: "#1F4E7A", pos: 100 }] },
  },
  {
    name: "Mono",
    config: { angle: 180, smooth: true, stops: [{ hex: "#F5F5F5", pos: 0 }, { hex: "#A8A8A8", pos: 50 }, { hex: "#3A3A3A", pos: 100 }] },
  },
  {
    name: "Forest",
    config: { angle: 145, smooth: true, stops: [{ hex: "#E8F0D6", pos: 0 }, { hex: "#7DA67D", pos: 50 }, { hex: "#2C4A2E", pos: 100 }] },
  },
  {
    name: "Bloom",
    config: { angle: 135, smooth: true, stops: [{ hex: "#FBE4D0", pos: 0 }, { hex: "#F2D7DE", pos: 50 }, { hex: "#DDE0F4", pos: 100 }] },
  },
  {
    name: "Midnight",
    config: { angle: 200, smooth: true, stops: [{ hex: "#1F1B3A", pos: 0 }, { hex: "#3D2A5C", pos: 50 }, { hex: "#0F1B2D", pos: 100 }] },
  },
];

export const GradientBuilder = ({
  value,
  onChange,
}: Props) => {
  const setAngle = (angle: number) => onChange({ ...value, angle });
  const setSmooth = (smooth: boolean) => onChange({ ...value, smooth });

  const updateStop = (i: number, patch: Partial<{ hex: string; pos: number }>) => {
    const stops = value.stops.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    onChange({ ...value, stops });
  };

  const addStop = () => {
    if (value.stops.length >= MAX_STOPS) return;
    const sorted = [...value.stops].sort((a, b) => a.pos - b.pos);
    // insert at the largest gap
    let bestGap = 0;
    let insertPos = 50;
    let insertHex = sorted[0]?.hex ?? "#888888";
    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = sorted[i + 1].pos - sorted[i].pos;
      if (gap > bestGap) {
        bestGap = gap;
        insertPos = (sorted[i].pos + sorted[i + 1].pos) / 2;
        insertHex = sorted[i].hex;
      }
    }
    onChange({ ...value, stops: [...value.stops, { hex: insertHex, pos: insertPos }] });
  };

  const removeStop = (i: number) => {
    if (value.stops.length <= MIN_STOPS) return;
    onChange({ ...value, stops: value.stops.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="min-w-0 space-y-4 pt-3">
      <div
        className="h-16 w-full rounded-md border border-border"
        style={{ background: gradientToCss(value) }}
        aria-label="Gradient preview"
      />

      <div>
        <Label className="text-xs">Presets</Label>
        <div className="mt-2 grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-3">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              type="button"
              onClick={() => onChange(p.config)}
              className="group flex min-h-[4.75rem] min-w-0 flex-col items-stretch gap-1 rounded-md border border-border p-1.5 transition-colors hover:border-primary"
              aria-label={`Apply ${p.name} preset`}
            >
              <div className="h-9 w-full rounded" style={{ background: gradientToCss(p.config) }} />
              <span className="text-[11px] text-muted-foreground group-hover:text-foreground">{p.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Angle</Label>
          <span className="text-xs tabular-nums text-muted-foreground">{value.angle}°</span>
        </div>
        <Slider
          value={[value.angle]}
          min={0}
          max={360}
          step={1}
          onValueChange={(v) => setAngle(v[0])}
          className="mt-2"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Color stops ({value.stops.length}/{MAX_STOPS})</Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addStop}
            disabled={value.stops.length >= MAX_STOPS}
            className="h-7 px-2"
          >
            <Plus className="h-3 w-3" /> Add
          </Button>
        </div>
        {value.stops.map((stop, i) => (
          <div key={i} className="min-w-0 space-y-3 rounded-md border border-border p-3">
            <div className="flex min-w-0 items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="h-9 w-9 shrink-0 rounded border border-border"
                    style={{ background: stop.hex }}
                    aria-label="Edit stop color"
                  />
                </PopoverTrigger>
                <PopoverContent className="w-56 space-y-2">
                  <HexColorPicker color={stop.hex} onChange={(hex) => updateStop(i, { hex })} style={{ width: "100%" }} />
                  <Input value={stop.hex} onChange={(e) => updateStop(i, { hex: e.target.value })} />
                </PopoverContent>
              </Popover>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium">Stop {i + 1}</p>
                <p className="truncate text-[11px] text-muted-foreground">Tap the swatch to edit {stop.hex}</p>
              </div>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{stop.pos}%</span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => removeStop(i)}
                disabled={value.stops.length <= MIN_STOPS}
                className="h-8 w-8 shrink-0 p-0"
                aria-label="Remove stop"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Slider
              value={[stop.pos]}
              min={0}
              max={100}
              step={1}
              onValueChange={(v) => updateStop(i, { pos: v[0] })}
            />
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Label className="text-xs">Smooth blend</Label>
          <p className="text-[11px] text-muted-foreground">Adds OKLCH midpoints for richer transitions.</p>
        </div>
        <Switch checked={value.smooth} onCheckedChange={setSmooth} />
      </div>
    </div>
  );
};
